/**
 * FiscalContextPdvDesktopService
 *
 * Gerencia o contexto fiscal local do PDV Desktop (Electron).
 *
 * Responsabilidades:
 *  1. Sincronizar dados fiscais via GET /api/offline/fiscal-context.
 *  2. Persistir emitente em emitente_local.
 *  3. Persistir configuração NFC-e em configuracao_nfce_local.
 *  4. Validar se o contexto fiscal está completo antes de emitir NFC-e.
 *
 * Campos obrigatórios para emissão:
 *  Emitente:  cnpj, ie, crt, codigo_municipio, municipio, uf
 *  Config:    serie, ambiente, idTokenCSC, tokenCSC
 */
import { getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE } from "@/modules/Application";
import TerminalConfigService from "./TerminalConfigService";

function _getBaseUrl() {
  return (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
}

const CAMPOS_EMITENTE = [
  { campo: "cnpj",             label: "CNPJ do emitente" },
  { campo: "ie",               label: "Inscrição Estadual" },
  { campo: "crt",              label: "CRT (Código Regime Tributário)" },
  { campo: "codigo_municipio", label: "Código IBGE do município" },
  { campo: "municipio",        label: "Município" },
  { campo: "uf",               label: "UF" },
];

const CAMPOS_CONFIG = [
  { campo: "serie",      label: "Série NFC-e" },
  { campo: "ambiente",   label: "Ambiente (1=produção / 2=homologação)" },
  { campo: "idTokenCSC", label: "ID Token CSC" },
  { campo: "tokenCSC",   label: "Token CSC" },
];

const FiscalContextPdvDesktopService = {
  /**
   * Busca dados fiscais do backend e persiste localmente.
   * Endpoint: GET /api/offline/fiscal-context/{uuid_terminal}
   * Resposta esperada: { emitente: {}, configuracao_nfce: {}, terminal?: {} }
   */
  async sincronizarContextoFiscal() {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });

    const [apiToken, uuidTerminal] = await Promise.all([
      api.config.get("api_token"),
      api.config.get("uuid_terminal"),
    ]);
    if (!apiToken) throw new Error("Token de autenticação não encontrado. Faça login novamente.");
    if (!uuidTerminal) throw new Error("UUID do terminal não encontrado. Configure o terminal antes de sincronizar.");

    const baseUrl = _getBaseUrl();
    const resp = await fetch(`${baseUrl}/api/offline/fiscal-context?uuid_terminal=${uuidTerminal}`, {
      headers: {
        Authorization: `Bearer ${apiToken}`,
        Accept:        "application/json",
      },
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => resp.statusText);
      throw new Error(`Erro ao buscar contexto fiscal (${resp.status}): ${text}`);
    }

    const json = await resp.json();
    const dados = json.data ?? json;
    const emitente = dados.emitente ?? null;
    const configuracao_nfce =
      dados.configuracao_nfce ??
      dados.nfce_config ??
      dados.configuracaoNfce ??
      null;

    if (emitente) {
      await api.emitente.salvarEmitente(emitente);
    }

    if (configuracao_nfce) {
      await TerminalConfigService.salvarConfiguracaoNfceTerminal({
        ...configuracao_nfce,
        payload_json: dados,
      });
    } else {
      throw new Error(TerminalConfigService.mensagemConfiguracaoNfceAusente());
    }

    // Atualiza o controle de sincronização
    await api.sync.atualizarControle("fiscal_context", new Date().toISOString());

    return { emitente, configuracao_nfce };
  },

  /**
   * Retorna o contexto fiscal armazenado localmente.
   * @returns {{ emitente: object|null, configuracao: object|null }}
   */
  async buscarContextoFiscalLocal() {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    const [emitente, configuracao] = await Promise.all([
      api.emitente.buscarEmitente(),
      api.configuracaoNfce.buscarConfiguracao(),
    ]);

    return { emitente, configuracao };
  },

  /**
   * Valida se todos os campos necessários para emissão estão preenchidos.
   * @returns {{ valido: boolean, problemas: string[], emitente: object|null, configuracao: object|null }}
   */
  async validarContextoFiscalLocal() {
    const { emitente, configuracao } = await this.buscarContextoFiscalLocal();
    const problemas = [];

    if (!emitente) {
      return {
        valido: false,
        problemas: ["Dados do emitente não encontrados. Sincronize o contexto fiscal antes de emitir NFC-e."],
        emitente:     null,
        configuracao: null,
      };
    }

    if (!configuracao) {
      return {
        valido: false,
        problemas: [TerminalConfigService.mensagemConfiguracaoNfceAusente()],
        emitente,
        configuracao: null,
      };
    }

    for (const { campo, label } of CAMPOS_EMITENTE) {
      if (!emitente[campo]) problemas.push(`${label} não configurado no emitente.`);
    }
    if (!emitente.certificado_configurado) {
      problemas.push("Certificado digital A1 não configurado no emitente.");
    }
    if (!emitente.certificado_caminho) {
      problemas.push("Arquivo do certificado digital A1 não configurado no emitente.");
    }

    for (const { campo, label } of CAMPOS_CONFIG) {
      if (!configuracao[campo]) problemas.push(`${label} não configurado.`);
    }

    return {
      valido: problemas.length === 0,
      problemas,
      emitente,
      configuracao,
    };
  },
};

export default FiscalContextPdvDesktopService;
